import React, { useState } from 'react';
import { Avatar, Typography, Tooltip, FormControlLabel, Checkbox } from '@mui/material';
import { OfferDTO } from '../../user/on-sale/types';
import { formatMoney, moneyFromMinor } from '../../utils/UnitConversion.utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useCurrency } from '../../../hooks/useCurrency';
import SteamAccountUserSelect from '../../utils/SteamAccountUserSelect';
import BaseModal from '../../utils/BaseModal';
import { SteamAccount } from '../../../store/slices/authSlice';
import { RARITY_COLORS, RarityGrade } from '../../utils/RarityColors';

type Props = {
  open: boolean;
  loading: boolean;
  offer: OfferDTO;
  selectedSteamAccount: SteamAccount | null;
  setSelectedSteamAccount: (account: SteamAccount | null) => void;
  onClose: () => void;
  onConfirm: () => void;
};

const BuyConfirmModal: React.FC<Props> = ({
                                            open,
                                            loading,
                                            offer,
                                            selectedSteamAccount,
                                            setSelectedSteamAccount,
                                            onClose,
                                            onConfirm,
                                          }) => {
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);
  const { convertFromUSD } = useCurrency();
  const steamAccounts = useSelector((state: RootState) => state.auth.steamAccounts);
  const [agreed, setAgreed] = useState<boolean>(false);

  const { item } = offer;
  const { details, catalog } = item;

  const rarityKey = typeof details?.rarity === 'string' ? details.rarity : '';
  const rarityColor = RARITY_COLORS[rarityKey as RarityGrade] ?? '#374151';
  const floatValue = details?.floatValue ?? null;
  const floatPercent = floatValue === null ? 0 : Math.min(Math.max(floatValue * 100, 0), 100);

  const renderAttachments = () => {
    const attachments = item.attachments || [];
    if (attachments.length === 0) return null;

    return (
        <div className="absolute bottom-3 left-2 flex flex-col gap-1 z-20">
          {attachments.map((att, idx) => (
              <Tooltip key={att.id || idx} title={att.marketHashName} arrow placement="right">
                <div className="bg-black/30 backdrop-blur-sm p-0.5 rounded border border-white/5 hover:border-white/20 transition-all duration-200">
                  <img
                      src={att.imageLink || ''}
                      alt="sticker"
                      className="w-6 h-6 object-contain drop-shadow-sm"
                  />
                </div>
              </Tooltip>
          ))}
        </div>
    );
  };

  const showAccountSelect = steamAccounts.filter((a) => a.tradeable).length > 1;

  const handleConfirm = () => {
    if (agreed && !loading) {
      onConfirm();
    }
  };

  return (
      <BaseModal
          open={open}
          title={catalog.groupName ?? 'Unknown Item'}
          submitText={loading ? 'Processing...' : 'Confirm Purchase'}
          onAbort={onClose}
          onSubmit={handleConfirm}
          fullWidthSubmit={true}
          maxWidth="xs"
          submitDisabled={!agreed || loading}
      >
        <div className="flex flex-col gap-5 select-none">
          {/* Header Badges Area */}
          <div className="flex flex-wrap items-center gap-1.5">
            {catalog?.wearName && (
                <span className="px-2 py-0.5 bg-blue-600/20 border border-blue-500/30 rounded text-[10px] font-black uppercase tracking-wider text-blue-600 dark:text-blue-400">
              {catalog.wearName.replace('_', ' ')}
            </span>
            )}
            {catalog.isStattrak && (
                <span className="px-2 py-0.5 bg-orange-600/20 border border-orange-500/30 rounded text-[9px] font-black uppercase tracking-wider text-orange-600 dark:text-orange-500">
              StatTrak™
            </span>
            )}
            {details?.phase && (
                <span className="px-2 py-0.5 bg-purple-600/20 border border-purple-500/30 rounded text-[9px] font-black uppercase tracking-wider text-purple-600 dark:text-purple-400">
              {details.phase}
            </span>
            )}
          </div>

          {/* Float Bar Wrapper */}
          {floatValue !== null && (
              <div className="flex items-center gap-3 bg-neutral-100 dark:bg-neutral-900/60 p-2 rounded-lg border border-black/5 dark:border-white/5 w-full">
                <div className="text-[10px] font-mono font-bold text-neutral-600 dark:text-neutral-400">
                  {floatValue.toFixed(10)}
                </div>
                <div className="flex-1">
                  <div
                      className="relative h-1 rounded-full w-full overflow-hidden bg-neutral-300 dark:bg-neutral-800"
                      style={{
                        background: 'linear-gradient(to right, #14532d 0%, #22c55e 15%, #eab308 38%, #f87171 45%, #b91c1c 100%)',
                      }}
                  >
                    <div
                        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_4px_white]"
                        style={{ left: `${floatPercent}%` }}
                    />
                  </div>
                </div>
              </div>
          )}

          {/* Item Preview Card */}
          <div
              className="relative h-52 flex items-center justify-center rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-900/40 border border-black/5 dark:border-white/5"
              style={{
                backgroundImage: `linear-gradient(to top, ${rarityColor}44 0%, rgba(26, 29, 36, 0) 115%)`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100% 100%'
              }}
          >
            <div className="absolute top-3 right-3 text-right z-20">
              <p className="text-[8px] font-black uppercase tracking-[0.2em] text-neutral-400 dark:text-white/30 leading-none">Rarity</p>
              <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: rarityColor }}>
                {details?.rarity?.replace('_', ' ')}
              </p>
            </div>

            <img
                src={catalog.imageLink || ''}
                alt="item"
                className="w-full max-w-[240px] object-contain z-10 drop-shadow-[0_10px_25px_rgba(0,0,0,0.4)]"
            />

            {renderAttachments()}

            <div
                className="absolute bottom-0 left-0 right-0 h-1"
                style={{ backgroundColor: rarityColor }}
            />
          </div>

          {/* Selection & Price Area */}
          <div className="flex flex-col gap-4">
            {showAccountSelect && (
                <div className="p-3.5 rounded-xl bg-neutral-100 dark:bg-neutral-900/60 border border-neutral-200 dark:border-white/5">
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="w-1 h-3 bg-blue-500 rounded-full" />
                    <Typography className="text-[10px] !font-semibold uppercase tracking-[0.15em] text-neutral-500 dark:text-neutral-400 leading-none">
                      Steam Delivery Account
                    </Typography>
                  </div>
                  <div className="w-full">
                    <SteamAccountUserSelect
                        value={selectedSteamAccount}
                        size="small"
                        variant="outlined"
                        placeholder={false}
                        onChange={(account: SteamAccount | null) => setSelectedSteamAccount(account)}
                    />
                  </div>
                </div>
            )}

            {/* Legal Acceptance Area */}
            <div className="px-1 pt-1">
              <FormControlLabel
                  control={
                    <Checkbox
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        size="small"
                        sx={{
                            padding: '4px',
                            color: '#737373',
                            '&.Mui-checked': {
                                color: '#3f51b5'
                            }
                        }}
                    />
                  }
                  label={
                    <span className="text-[12px] text-neutral-500 dark:text-neutral-400 select-none leading-normal">
                      I agree to the{' '}
                      <a
                          href="/tos"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-dark-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                      >
                        Terms of Service
                      </a>{' '}
                      &{' '}
                      <a
                          href="/refund-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-dark-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                      >
                        Refund Policy
                      </a>
                    </span>
                  }
                  className="!items-start !m-0 gap-1.5"
              />
            </div>

            {/* Price Summary Container */}
            <div className="flex flex-col gap-1 px-1">
              <div className="flex items-center justify-between pt-2 border-t border-black/5 dark:border-white/5">
                <span className="text-xs font-black uppercase tracking-[0.1em] text-neutral-900 dark:text-white">Total</span>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm font-bold text-dark-primary">{currencySymbol}</span>
                  <span className="text-xl font-black text-neutral-900 dark:text-white">
                  {formatMoney(convertFromUSD(moneyFromMinor(offer.priceMinor)))}
                </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BaseModal>
  );
};

export default BuyConfirmModal;
